import { ButtonAnchor, CartButton, P1, P2 } from '@zooplus/ui-components';
import { getUrlLocalised, useTranslation } from '../../hooks/use-translations';
import React, { useEffect, useRef, useState } from 'react';
import styles from './PdpArticle.module.scss';
import { ArticleImage } from '../common/article-image/ArticleImage';
import { ProductInfo } from '../../model/product';
import { CartArticleV2, useCartContext } from '@zooplus/cart-provider';
import { BoxWithCta } from '../common/box-with-cta/BoxWithCta';

interface PdpArticleProps {
  articleInfo: ProductInfo;
}

export const PdpArticle = ({ articleInfo }: PdpArticleProps) => {
  const { t } = useTranslation();
  const { changeArticleQuantity, cart } = useCartContext();
  const [addingToCart, setAddingToCart] = useState(false);
  const [alreadyAdded, setAlreadyAdded] = useState(false);
  const addToCartRef = useRef(null);

  function addToCart() {
    setAddingToCart(true);
    changeArticleQuantity({
      articleId: articleInfo.articleShopId,
      quantity: 1,
      subscriptionSelected: true,
      popover: addToCartRef.current ? { trigger: addToCartRef.current } : undefined,
    }).then((_) => setAddingToCart(false));
  }

  useEffect(() => {
    setAlreadyAdded(
      cart?.articles?.some((article: CartArticleV2) => article.id === articleInfo.articleShopId) ||
        false,
    );
  }, [cart]);

  const content = (
    <div className={styles.articleInfo}>
      <ArticleImage pictureUrl={articleInfo.pictureUrl} hasAusIcon={true} small={true} />
      <div className={styles.articleDescription}>
        <P1 bold>{articleInfo.productName}</P1>
        <P2 bold className={styles.variantName}>
          {articleInfo.variantName}
        </P2>
      </div>
    </div>
  );

  const buttons = [
    <ButtonAnchor
      id={`back-to-pdp-${articleInfo.articleShopId}`}
      key={`back-to-pdp-${articleInfo.articleShopId}`}
      data-zta={'back-to-pdp'}
      href={getUrlLocalised(t, articleInfo.pdpUrl)}
      size={'normal'}
      length={'full'}
      variant={'secondary'}
      className={styles.button}>
      {t('pdp-article.back')}
    </ButtonAnchor>,
    !alreadyAdded ? (
      <CartButton
        id={`add-to-subscription-${articleInfo.articleShopId}`}
        key={`add-to-subscription-${articleInfo.articleShopId}`}
        data-zta={'add-to-subscription'}
        className={styles.button}
        size={'normal'}
        withIcon
        onClick={addToCart}
        isLoading={addingToCart}
        ref={addToCartRef}>
        {t('pdp-article.add-to-cart')}
      </CartButton>
    ) : (
      <ButtonAnchor
        id={`go-to-cart-${articleInfo.articleShopId}`}
        key={`go-to-cart-${articleInfo.articleShopId}`}
        data-zta={'go-to-cart'}
        href={getUrlLocalised(t, '/checkout/cart')}
        size={'normal'}
        length={'full'}
        variant={'primary'}
        className={styles.button}>
        {t('pdp-article.go-to-cart')}
      </ButtonAnchor>
    ),
  ];

  return (
    <BoxWithCta
      titleKey={'pdp-article.title'}
      content={content}
      contentUrl={getUrlLocalised(t, articleInfo.pdpUrl)}
      buttons={buttons}
    />
  );
};
